import { useCallback, useRef } from "react";
import { useCanvasStore } from "@/store/useCanvasStore";
import { resolveCanvasTarget, toggleInSet, stripSelectChanges, type CanvasTarget } from "./mobileSelection";

// Mobile long-press → context menu tuning.
const MOBILE_LONG_PRESS_MS = 500;
const MOBILE_MOVE_CANCEL_PX = 12;

type NodeChanges = Parameters<ReturnType<typeof useCanvasStore.getState>["onNodesChange"]>[0];
type EdgeChanges = Parameters<ReturnType<typeof useCanvasStore.getState>["onEdgesChange"]>[0];

export interface MobileSelectionCtx {
  type: "node" | "edge" | "shape";
  id: string;
}

/**
 * Touch selection for the mobile canvas: additive tap to (de)select, and a
 * long-press that opens the shared context menu. When `enabled` is false (desktop
 * or drawing tools) every handler is a pass-through, so ReactFlow's native
 * behaviour is untouched.
 *
 * `openCtxMenu` is called with screen coords + the resolved target when a
 * long-press fires — the caller renders the same menu used by desktop right-click.
 */
export function useMobileSelection(
  enabled: boolean,
  openCtxMenu: (x: number, y: number, ctx: MobileSelectionCtx) => void,
) {
  const setSelection = useCanvasStore((s) => s.setSelection);
  const onNodesChange = useCanvasStore((s) => s.onNodesChange);
  const onEdgesChange = useCanvasStore((s) => s.onEdgesChange);

  const lpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lpStart = useRef<{ x: number; y: number; target: CanvasTarget } | null>(null);
  // Set true when a long-press fires; swallows the trailing click so it doesn't
  // toggle selection or close the just-opened menu. Cleared on the next touch.
  const suppressClick = useRef(false);

  const clearLongPress = useCallback(() => {
    if (lpTimer.current) { clearTimeout(lpTimer.current); lpTimer.current = null; }
    lpStart.current = null;
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    suppressClick.current = false;
    if (!enabled || e.touches.length !== 1) { clearLongPress(); return; }
    const target = resolveCanvasTarget(e.target as Element);
    if (!target) { clearLongPress(); return; }
    const t = e.touches[0];
    lpStart.current = { x: t.clientX, y: t.clientY, target };
    if (lpTimer.current) clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => {
      const s = lpStart.current;
      lpTimer.current = null;
      if (!s) return;
      suppressClick.current = true;
      openCtxMenu(s.x, s.y, { type: s.target.type, id: s.target.id });
    }, MOBILE_LONG_PRESS_MS);
  }, [enabled, clearLongPress, openCtxMenu]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const s = lpStart.current;
    if (!s) return;
    const t = e.touches[0];
    if (Math.hypot(t.clientX - s.x, t.clientY - s.y) > MOBILE_MOVE_CANCEL_PX) clearLongPress();
  }, [clearLongPress]);

  // Additive tap: toggle the tapped element in/out of the selection.
  const toggleSelect = useCallback((rfId: string) => {
    if (suppressClick.current) return;
    setSelection(toggleInSet(useCanvasStore.getState().selectedIds, rfId));
  }, [setSelection]);

  const clearSelectionOnPane = useCallback(() => {
    if (suppressClick.current) return;
    if (useCanvasStore.getState().selectedIds.size > 0) setSelection(new Set());
  }, [setSelection]);

  // Should the trailing click after a long-press be swallowed?
  const shouldSwallowClick = useCallback(() => suppressClick.current, []);

  // On mobile we drive selection ourselves, so strip RF's built-in select changes.
  const handleNodesChange = useCallback((changes: NodeChanges) => {
    onNodesChange(enabled ? stripSelectChanges(changes) : changes);
  }, [enabled, onNodesChange]);

  const handleEdgesChange = useCallback((changes: EdgeChanges) => {
    onEdgesChange(enabled ? stripSelectChanges(changes) : changes);
  }, [enabled, onEdgesChange]);

  return {
    onTouchStart,
    onTouchMove,
    clearLongPress,
    toggleSelect,
    clearSelectionOnPane,
    shouldSwallowClick,
    handleNodesChange,
    handleEdgesChange,
  };
}
